import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { classicSoftwareBacklogEmbedRoute } from '@atlassian/jira-router-routes-software-classic-backlog-routes/src/classicSoftwareBacklogEmbedRoute.tsx';
import { RapidBoardSpaPage } from '@atlassian/jira-router-routes-user-board-entries/src/ui/async.tsx';
import { chromelessLayout } from '@atlassian/jira-chromeless-layout/src/index.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	getUIFBacklogDataResource,
} from '@atlassian/jira-router-resources-classic-projects/src/services/main.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import {
	forgeProjectModuleResource,
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { epicsLiteResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/epics-lite/epics-lite-resource.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { getEditModelResource } from '@atlassian/jira-router-resources-classic-projects/src/services/edit-model/index.tsx';
import { resourceWithCondition } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { getBoardDetailsResource } from '@atlassian/jira-router-resources-classic-projects/src/services/board-details/index.tsx';
import { jiraSpaResources } from '@atlassian/jira-router-resources-spa-page-container/src/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@atlassian/jira-common-constants/src/ssr-early-assets.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { aiEntitlementResource } from '@atlassian/jira-router-resources-ai-entitlement/src/services/index.tsx';
import { backlogCMPViewSettingsResource } from '@atlassian/jira-router-resources-uif-backlog';
import { IS_MODERN_SSR } from '@atlassian/jira-conditional-ssr/src/constants.tsx';
import { LazyBacklogPage } from './ui/LazyBacklogPage.tsx';
import { classicBacklogCapability } from './common/constants.tsx';

export const classicSoftwareBacklogEmbedRouteEntry = createEntry(
	classicSoftwareBacklogEmbedRoute,
	() => ({
		group: 'classic-software',
		ufoName: 'classic-backlog-embed',
		component: RapidBoardSpaPage,
		layout: chromelessLayout,

		resources: [
			...getUIFBacklogDataResource(true),
			...getRapidboardResources(),
			...getNavigationResources(),
			// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
			incidentExperimentTraitResource,
			projectContextResource,
			forgeProjectModuleResource,
			staticSidebarShortcutsResource,
			staticProjectSidebarNavigationResource,
			staticProjectSidebarConnectAddonsNavigationResource,
			// end of resources expanded from getNavigationSidebarProjectResource
			getRapidBoardDataResource(),
			getEditModelResource(),
			getBoardDetailsResource(),
			forgeBacklogModuleResource,
			forgeSprintModuleResource,
			...jiraSpaResources(),
			aiEntitlementResource,
			resourceWithCondition(
				() =>
					expVal('backlog-preload-epics-ssr', 'isEnabled', false) &&
					(!IS_MODERN_SSR || fg('enable_ssr_for_cmp_backlog')),
				epicsLiteResource,
			),
			resourceWithCondition(
				() => fg('backlog-hydrate-view-settings-from-backend'),
				backlogCMPViewSettingsResource,
			),
		],

		afterPaint: [LazyIssueApp],
		forPaint: [LazyBacklogPage],

		preloadOptions: {
			earlyChunks: ['software-cmp-backlog-early-entry'],
		},

		meta: {
			capability: classicBacklogCapability,
		},

		earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,

		bifrost: {
			bifrostGroup: 'shell-only',

			bundling: {
				packageName:
					'@atlassian/jira-router-routes-software-classic-backlog-entries/src/classicSoftwareBacklogEmbedRouteEntry.tsx',
				whichExportToUse: 'classicSoftwareBacklogEmbedRouteEntry',
			},

			enabled: true,
		},
		excludeFromWrmDeferredLoading: true,
	}),
);
